import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { PaymentForm, CreditCard } from 'react-square-web-payments-sdk';
import {
  Crown, Check, ArrowRight, Percent, Star, Calendar, Ship, Mail, Phone, User,
  Lock, ShieldCheck, Anchor, Waves, Utensils, Wine, Calculator, TrendingUp, Minus, Plus, Users
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;

const SavingsSimulator = ({ cards, loading, onSelectCard }) => {
  const [passengers, setPassengers] = useState(2);
  const [cruisePrice, setCruisePrice] = useState(1970);
  const [cruisesPerYear, setCruisesPerYear] = useState(1);

  if (loading || cards.length === 0) return null;

  const priceOptions = [
    { label: '1 490€', value: 1490 },
    { label: '1 690€', value: 1690 },
    { label: '1 970€', value: 1970 },
    { label: '2 490€', value: 2490 },
    { label: '3 490€', value: 3490 },
  ];

  return (
    <div className="bg-[#F8F9FA] rounded-2xl border border-slate-200 overflow-hidden">
      {/* Controls */}
      <div className="p-6 md:p-8 bg-white border-b border-slate-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Passengers */}
          <div>
            <label className="text-sm font-semibold text-[#0A2540] mb-2 block">
              <Users className="h-4 w-4 inline mr-1.5 text-[#2A9D8F]" />
              Nombre de passagers
            </label>
            <p className="text-xs text-[#94A3B8] mb-3">1 carte par passager requise</p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPassengers(p => Math.max(1, p - 1))}
                className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                data-testid="sim-passengers-minus"
              >
                <Minus className="h-4 w-4 text-[#4A5568]" />
              </button>
              <div className="flex-1 text-center">
                <span className="text-3xl font-display font-bold text-[#0A2540]">{passengers}</span>
                <p className="text-xs text-[#94A3B8]">passager{passengers > 1 ? 's' : ''}</p>
              </div>
              <button
                onClick={() => setPassengers(p => Math.min(8, p + 1))}
                className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                data-testid="sim-passengers-plus"
              >
                <Plus className="h-4 w-4 text-[#4A5568]" />
              </button>
            </div>
          </div>

          {/* Cruise price */}
          <div>
            <label className="text-sm font-semibold text-[#0A2540] mb-2 block">
              <Ship className="h-4 w-4 inline mr-1.5 text-[#2A9D8F]" />
              Prix par personne
            </label>
            <p className="text-xs text-[#94A3B8] mb-3">Tarif de la croisière choisie</p>
            <div className="flex flex-wrap gap-2">
              {priceOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setCruisePrice(opt.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    cruisePrice === opt.value
                      ? 'bg-[#2A9D8F] text-white shadow-sm'
                      : 'bg-white border border-slate-200 text-[#4A5568] hover:border-[#2A9D8F]/40'
                  }`}
                  data-testid={`sim-price-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Cruises per year */}
          <div>
            <label className="text-sm font-semibold text-[#0A2540] mb-2 block">
              <Calendar className="h-4 w-4 inline mr-1.5 text-[#2A9D8F]" />
              Croisières par an
            </label>
            <p className="text-xs text-[#94A3B8] mb-3">Nombre de semaines par an</p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCruisesPerYear(c => Math.max(1, c - 1))}
                className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                data-testid="sim-cruises-minus"
              >
                <Minus className="h-4 w-4 text-[#4A5568]" />
              </button>
              <div className="flex-1 text-center">
                <span className="text-3xl font-display font-bold text-[#0A2540]">{cruisesPerYear}</span>
                <p className="text-xs text-[#94A3B8]">semaine{cruisesPerYear > 1 ? 's' : ''} / an</p>
              </div>
              <button
                onClick={() => setCruisesPerYear(c => Math.min(4, c + 1))}
                className="w-10 h-10 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors"
                data-testid="sim-cruises-plus"
              >
                <Plus className="h-4 w-4 text-[#4A5568]" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {cards.map((card) => {
            const cardCostTotal = card.price * passengers;
            const totalCruiseCost = cruisePrice * passengers * cruisesPerYear;
            const discountPerCruise = cruisePrice * (card.discount_percent / 100);
            const totalSavings = discountPerCruise * passengers * cruisesPerYear;
            const netSavings = totalSavings - cardCostTotal;
            const yearsOfCard = card.duration_months / 12;
            const totalSavingsOverDuration = (discountPerCruise * passengers * cruisesPerYear * yearsOfCard) - cardCostTotal;
            const isPopular = card.duration_months === 24;
            const isPositive = netSavings > 0;

            return (
              <div
                key={card.id}
                className={`bg-white rounded-xl border-2 p-5 transition-all hover:shadow-lg ${isPopular ? 'border-[#2A9D8F] shadow-md' : 'border-slate-200'}`}
                data-testid={`sim-result-${card.slug}`}
              >
                {isPopular && (
                  <div className="text-center mb-3">
                    <span className="px-3 py-1 bg-[#2A9D8F] text-white text-xs font-bold rounded-full">Meilleur rapport</span>
                  </div>
                )}
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-2" style={{ backgroundColor: `${card.color}15` }}>
                    <span className="text-2xl font-display font-bold" style={{ color: card.color }}>-{card.discount_percent}%</span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-[#0A2540]">{card.name}</h3>
                  <p className="text-xs text-[#94A3B8]">{card.duration_months} mois — {card.price}€/carte</p>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1.5 border-b border-slate-100">
                    <span className="text-[#4A5568]">Coût {passengers} carte{passengers > 1 ? 's' : ''}</span>
                    <span className="font-medium text-[#0A2540]">{cardCostTotal}€</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-slate-100">
                    <span className="text-[#4A5568]">Remise / croisière / pers.</span>
                    <span className="font-medium text-[#2A9D8F]">-{discountPerCruise.toFixed(0)}€</span>
                  </div>
                  <div className="flex justify-between py-1.5 border-b border-slate-100">
                    <span className="text-[#4A5568]">Économie / an</span>
                    <span className="font-bold text-[#2A9D8F]">-{totalSavings.toFixed(0)}€</span>
                  </div>
                  <div className={`flex justify-between py-2 rounded-lg px-2 mt-1 ${isPositive ? 'bg-green-50' : 'bg-amber-50'}`}>
                    <span className="font-semibold text-[#0A2540]">Gain net / an</span>
                    <span className={`font-bold text-lg ${isPositive ? 'text-green-600' : 'text-amber-600'}`}>
                      {isPositive ? '+' : ''}{netSavings.toFixed(0)}€
                    </span>
                  </div>

                  {yearsOfCard > 1 && (
                    <div className="flex justify-between py-2 px-2 rounded-lg bg-[#0A2540] mt-2">
                      <span className="text-white/80 text-xs">Gain total sur {yearsOfCard} ans</span>
                      <span className="font-bold text-white flex items-center gap-1">
                        <TrendingUp className="h-3.5 w-3.5" />
                        {totalSavingsOverDuration > 0 ? '+' : ''}{totalSavingsOverDuration.toFixed(0)}€
                      </span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => onSelectCard(card)}
                  className="w-full mt-4 py-2.5 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90"
                  style={{ backgroundColor: card.color }}
                  data-testid={`sim-select-${card.slug}`}
                >
                  Choisir cette carte
                </button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-[#94A3B8] mt-6">
          Le nombre de cartes Club est limité à <strong>50 par an</strong>. Les économies sont calculées hors options et suppléments.
        </p>
      </div>
    </div>
  );
};

const ClubPage = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [step, setStep] = useState('browse'); // browse, form, payment, success
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [formData, setFormData] = useState({ user_name: '', user_email: '', user_phone: '' });
  const [purchaseData, setPurchaseData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/club-cards`).then(r => { setCards(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSelectCard = (card) => {
    setSelectedCard(card);
    setQuantity(1);
    setStep('form');
    setTimeout(() => document.getElementById('club-form')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.user_name || !formData.user_email || !formData.user_phone) {
      toast.error('Veuillez remplir tous les champs'); return;
    }
    setStep('payment');
  };

  const totalPrice = selectedCard ? selectedCard.price * quantity : 0;

  const handlePaymentToken = async (token) => {
    setPaymentProcessing(true);
    try {
      const response = await axios.post(`${API}/club-memberships/purchase`, {
        source_id: token.token,
        card_id: selectedCard.id,
        user_name: formData.user_name,
        user_email: formData.user_email,
        user_phone: formData.user_phone,
        quantity: quantity,
        verification_token: token.verificationToken || null,
      });
      if (response.data.success) {
        setPurchaseData(response.data.membership);
        setStep('success');
        toast.success('Bienvenue au Club des Voyageurs !');
      }
    } catch (error) {
      toast.error('Echec du paiement', { description: error.response?.data?.detail || 'Veuillez reessayer' });
    } finally {
      setPaymentProcessing(false);
    }
  };

  const calculateSavings = (card) => {
    const price = 1970;
    const discount = price * (card.discount_percent / 100);
    return { discount, afterDiscount: price - discount, net: discount - card.price };
  };

  // Success screen
  if (step === 'success') {
    return (
      <Layout>
        <section className="pt-32 pb-32 bg-[#F8F9FA] min-h-screen" data-testid="club-success">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="w-24 h-24 bg-[#2A9D8F] rounded-full flex items-center justify-center mx-auto mb-8">
              <Crown className="h-12 w-12 text-white" />
            </div>
            <h1 className="font-display text-4xl font-bold text-[#0A2540] mb-4">Bienvenue au Club !</h1>
            <p className="text-[#4A5568] text-lg mb-4">
              {quantity > 1
                ? <>{quantity} <strong>{selectedCard?.name}</strong> sont maintenant actives.</>
                : <>Votre <strong>{selectedCard?.name}</strong> est maintenant active.</>
              }
            </p>
            {purchaseData && (
              <div className="inline-block bg-white rounded-xl border border-[#2A9D8F]/20 p-4 mb-4 text-left">
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <span className="text-[#94A3B8]">Carte</span>
                  <span className="font-semibold text-[#0A2540]">{purchaseData.card_name}</span>
                  <span className="text-[#94A3B8]">Nombre</span>
                  <span className="font-semibold text-[#0A2540]">{purchaseData.cards_count} carte{purchaseData.cards_count > 1 ? 's' : ''}</span>
                  <span className="text-[#94A3B8]">Remise</span>
                  <span className="font-semibold text-[#2A9D8F]">-{purchaseData.discount_percent}%</span>
                  <span className="text-[#94A3B8]">Valable jusqu'au</span>
                  <span className="font-semibold text-[#C4724F]">{new Date(purchaseData.end_date).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>
            )}
            <p className="text-[#4A5568] mb-8">
              Un email de confirmation a ete envoye a <strong>{formData.user_email}</strong>.
              Votre remise de <strong>{selectedCard?.discount_percent}%</strong> sera appliquee
              automatiquement lors de votre prochaine réservation avec cet email.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="bg-[#C4724F] hover:bg-[#C4724F]/90 text-white font-semibold" size="lg">
                <Link to="/reservation-cabines-privatisation-catamaran-sognudimare">
                  Réserver ma croisière <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/destinations-croisieres-catamaran">Voir les destinations</Link>
              </Button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO
        title="Club des Voyageurs — Programme fidélité croisière catamaran"
        description="Rejoignez le Club des Voyageurs SOGNUDIMARE : −10 % à −20 % sur vos prochaines croisières catamaran en Corse, Sardaigne, Grèce, Caraïbes. Avantages exclusifs, embarquement prioritaire."
        path="/carte-club-des-voyageurs"
        keywords={[
          'club fidélité croisière',
          'remise croisière catamaran',
          'club voyageurs corse',
          'avantages fidélité',
          'programme fidélité bateau',
        ]}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#0A2540] overflow-hidden" data-testid="club-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6">
                <Crown className="h-4 w-4 text-[#C4724F]" />
                <span className="text-white/80 text-sm">Exclusivité SOGNUDIMARE</span>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-bold text-white mb-6">
                Le Club des <span className="italic text-[#2A9D8F]">Voyageurs</span>
              </h1>
              <p className="text-white/70 text-lg mb-4">
                Chaque année, profitez d'une ou deux semaines de croisière tout compris,
                avec équipage, en choisissant votre carte club des voyageurs.
              </p>
              <p className="text-white/70 mb-8">
                Vous bénéficierez de <strong className="text-[#C4724F]">10 a 20%</strong> sur votre
                prochaine croisière en Corse ou en Sardaigne.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                {[
                  { icon: Percent, text: 'Jusqu\'a 20% de remise' },
                  { icon: Calendar, text: 'Réservation prioritaire' },
                  { icon: Star, text: 'Places garanties chaque année' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full">
                    <b.icon className="h-3.5 w-3.5 text-[#2A9D8F]" /><span className="text-white text-xs">{b.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="shrink-0">
              <img
                src="https://static.wixstatic.com/media/ce6ce7_ab2c364fbfdd4f3d8aeb847f8cab900f~mv2.png/v1/crop/x_0,y_203,w_1080,h_944/fill/w_400,h_350,fp_0.50_0.50,q_90,enc_avif,quality_auto/cartes%20CLUB%20SOGNUDIMARE%20(1).png"
                alt="Cartes Club des Voyageurs SOGNUDIMARE"
                className="max-w-[360px] w-full"
                data-testid="club-hero-cards"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <img
              src="https://static.wixstatic.com/media/ce6ce7_ec12ec69477c40e9af1b48d6c2a8e05b~mv2.png/v1/crop/x_39,y_0,w_1028,h_1350/fill/w_300,h_394,fp_0.50_0.50,q_85,enc_avif,quality_auto/FONCTIONNEMENT%20CLUB%20SOGNUDIMARE%20(1).png"
              alt="Fonctionnement Club SOGNUDIMARE"
              className="mx-auto max-w-[240px] mb-8"
              data-testid="club-how-it-works-image"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Choisissez votre carte', desc: 'Engagement 12, 24 ou 36 mois' },
              { step: '02', title: 'Payez simplement', desc: 'Paiement sécurisé par carte bancaire' },
              { step: '03', title: 'Profitez des avantages', desc: '10 a 20% sur toutes les croisières' },
              { step: '04', title: 'Naviguez sereinement', desc: 'Semaines garanties chaque année' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 bg-[#2A9D8F] rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white font-display text-lg font-bold">{item.step}</span>
                </div>
                <h3 className="font-semibold text-[#0A2540] text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-[#4A5568]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Savings Simulator */}
      <section className="py-16 bg-white border-t border-slate-100" data-testid="club-simulator">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2A9D8F]/10 rounded-full mb-4">
              <Calculator className="h-4 w-4 text-[#2A9D8F]" />
              <span className="text-[#2A9D8F] text-sm font-medium">Simulateur</span>
            </div>
            <h2 className="font-display text-3xl font-bold text-[#0A2540] mb-3">
              Calculez vos <span className="italic text-[#2A9D8F]">économies</span>
            </h2>
            <p className="text-[#4A5568] max-w-xl mx-auto">
              Découvrez combien vous économisez avec votre Carte Club des Voyageurs selon le nombre de passagers et de croisières.
            </p>
          </div>

          <SavingsSimulator cards={cards} loading={loading} onSelectCard={handleSelectCard} />
        </div>
      </section>

      {/* Cards Section */}
      <section className="py-16 bg-[#F8F9FA]" data-testid="club-cards-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl font-bold text-[#0A2540] mb-3">
              Choisissez votre carte de membre
            </h2>
            <p className="text-[#4A5568]">Le nombre de cartes est limite a 50 par année</p>
          </div>

          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {cards.map((card) => {
                const savings = calculateSavings(card);
                const isPopular = card.duration_months === 24;
                return (
                  <Card key={card.id} className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl ${isPopular ? 'ring-2 ring-[#2A9D8F] md:scale-105' : ''}`} data-testid={`club-card-${card.slug}`}>
                    {isPopular && (
                      <div className="absolute top-4 right-4 px-3 py-1 bg-[#2A9D8F] text-white text-xs font-bold rounded-full z-10">Le + populaire</div>
                    )}
                    <div className="h-28 flex items-center justify-center" style={{ backgroundColor: card.color }}>
                      <div className="text-center text-white">
                        <div className="text-5xl font-display font-bold">-{card.discount_percent}%</div>
                        <div className="text-xs opacity-80">sur toutes les croisières</div>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-display text-xl font-bold text-[#0A2540] mb-1">{card.name}</h3>
                      <p className="text-[#4A5568] text-xs mb-4">{card.description}</p>
                      <div className="mb-4">
                        <span className="text-3xl font-display font-bold text-[#0A2540]">{card.price}€</span>
                        <span className="text-[#94A3B8] text-sm ml-1">/ {card.duration_months} mois</span>
                        <p className="text-xs text-[#94A3B8]">Soit {(card.price / (card.duration_months / 12)).toFixed(0)}€/an</p>
                      </div>
                      <ul className="space-y-2 mb-4 text-xs">
                        {card.advantages.slice(0, 4).map((a, i) => (
                          <li key={i} className="flex items-start gap-2"><Check className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: card.color }} /><span className="text-[#4A5568]">{a}</span></li>
                        ))}
                      </ul>
                      <div className="p-3 bg-[#F5F3EF] rounded-lg mb-4">
                        <p className="text-xs text-[#4A5568] mb-1">Exemple : croisière a 1 970€/pers</p>
                        <div className="flex justify-between text-xs"><span>Économie</span><span className="font-bold text-[#2A9D8F]">{savings.discount.toFixed(0)}€</span></div>
                        <div className="flex justify-between text-xs"><span>Gain net (après carte)</span><span className="font-bold" style={{ color: card.color }}>+{savings.net.toFixed(0)}€</span></div>
                      </div>
                      <Button onClick={() => handleSelectCard(card)} className="w-full text-white font-semibold" style={{ backgroundColor: card.color }} data-testid={`select-card-${card.slug}`}>
                        Choisir cette carte <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Form + Payment */}
      {selectedCard && (step === 'form' || step === 'payment') && (
        <section id="club-form" className="py-16 bg-white" data-testid="club-purchase-section">
          <div className="max-w-lg mx-auto px-4">
            <Card className="border-2" style={{ borderColor: `${selectedCard.color}40` }}>
              <div className="p-6" style={{ backgroundColor: selectedCard.color }}>
                <div className="flex items-center justify-between text-white">
                  <div>
                    <h3 className="font-display text-xl font-bold">{selectedCard.name}</h3>
                    <p className="text-sm opacity-80">-{selectedCard.discount_percent}% sur toutes les croisières</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-display font-bold">{totalPrice}€</div>
                    {quantity > 1 && <p className="text-xs opacity-80">{quantity} x {selectedCard.price}€</p>}
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                {step === 'form' && (
                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    {/* Quantity selector */}
                    <div>
                      <Label>Nombre de cartes *</Label>
                      <p className="text-xs text-[#94A3B8] mb-2">1 carte = 1 passager bénéficiaire de la remise</p>
                      <div className="flex items-center gap-3 mt-1">
                        <Button type="button" variant="outline" size="sm" onClick={() => setQuantity(q => Math.max(1, q - 1))} disabled={quantity <= 1} className="h-10 w-10" data-testid="qty-minus">-</Button>
                        <div className="flex-1 text-center">
                          <span className="text-2xl font-display font-bold" style={{ color: selectedCard.color }}>{quantity}</span>
                          <p className="text-xs text-[#94A3B8]">carte{quantity > 1 ? 's' : ''} = {quantity} passager{quantity > 1 ? 's' : ''}</p>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={() => setQuantity(q => Math.min(8, q + 1))} disabled={quantity >= 8} className="h-10 w-10" data-testid="qty-plus">+</Button>
                      </div>
                      {quantity > 1 && (
                        <p className="text-xs text-[#2A9D8F] mt-2 text-center font-medium">
                          {quantity} cartes x {selectedCard.price}€ = {totalPrice}€
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Nom complet *</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                        <Input value={formData.user_name} onChange={(e) => setFormData(p => ({...p, user_name: e.target.value}))} placeholder="Jean Dupont" className="pl-10" required data-testid="club-name-input" />
                      </div>
                    </div>
                    <div>
                      <Label>Email * <span className="text-xs text-[#94A3B8]">(lie a vos cartes club)</span></Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                        <Input type="email" value={formData.user_email} onChange={(e) => setFormData(p => ({...p, user_email: e.target.value}))} placeholder="jean@exemple.com" className="pl-10" required data-testid="club-email-input" />
                      </div>
                    </div>
                    <div>
                      <Label>Telephone *</Label>
                      <div className="relative mt-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
                        <Input value={formData.user_phone} onChange={(e) => setFormData(p => ({...p, user_phone: e.target.value}))} placeholder="+33 6 12 34 56 78" className="pl-10" required data-testid="club-phone-input" />
                      </div>
                    </div>
                    <Button type="submit" className="w-full text-white font-semibold" style={{ backgroundColor: selectedCard.color }}>
                      <Lock className="mr-2 h-4 w-4" /> Procéder au paiement de {totalPrice}€
                    </Button>
                  </form>
                )}

                {step === 'payment' && (
                  <div className="space-y-4">
                    <div className="p-3 bg-[#F5F3EF] rounded-lg text-sm">
                      <div className="flex justify-between"><span className="text-[#4A5568]">Carte</span><span className="font-medium">{selectedCard.name}</span></div>
                      <div className="flex justify-between"><span className="text-[#4A5568]">Quantite</span><span className="font-medium">{quantity} carte{quantity > 1 ? 's' : ''} ({quantity} passager{quantity > 1 ? 's' : ''})</span></div>
                      <div className="flex justify-between"><span className="text-[#4A5568]">Email</span><span className="font-medium">{formData.user_email}</span></div>
                      {quantity > 1 && <div className="flex justify-between text-xs mt-1"><span className="text-[#94A3B8]">Detail</span><span className="text-[#94A3B8]">{quantity} x {selectedCard.price}€</span></div>}
                      <div className="flex justify-between border-t pt-2 mt-2"><span className="font-semibold" style={{ color: selectedCard.color }}>Total</span><span className="text-xl font-bold" style={{ color: selectedCard.color }}>{totalPrice}€</span></div>
                    </div>
                    <div data-testid="club-square-payment">
                      <PaymentForm
                        applicationId={SQUARE_APP_ID}
                        locationId={SQUARE_LOCATION_ID}
                        cardTokenizeResponseReceived={handlePaymentToken}
                        createPaymentRequest={() => ({
                          countryCode: 'FR', currencyCode: 'EUR',
                          total: { amount: String(totalPrice), label: `SOGNUDIMARE - ${quantity}x ${selectedCard.name}` },
                        })}
                      >
                        <CreditCard
                          buttonProps={{ css: { backgroundColor: selectedCard.color, color: '#fff', fontSize: '16px', fontWeight: '600', padding: '12px', borderRadius: '8px' } }}
                          style={{ input: { fontSize: '16px', color: '#0A2540' }, 'input::placeholder': { color: '#94A3B8' }, '.input-container': { borderColor: '#e2e8f0', borderRadius: '8px' }, '.input-container.is-focus': { borderColor: '#2A9D8F' } }}
                        >
                          {paymentProcessing ? 'Traitement...' : `Payer ${totalPrice}€`}
                        </CreditCard>
                      </PaymentForm>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-xs text-[#94A3B8]">
                      <ShieldCheck className="h-4 w-4" /> Paiement sécurisé par Square
                    </div>
                    <div className="flex items-center justify-center gap-3 mt-1" data-testid="club-payment-icons">
                      <svg viewBox="0 0 48 32" className="h-5 w-auto"><rect width="48" height="32" rx="4" fill="#1A1F71"/><text x="24" y="20" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold" fontFamily="Arial, sans-serif">CB</text></svg>
                      <svg viewBox="0 0 48 32" className="h-5 w-auto" fill="none"><rect width="48" height="32" rx="4" fill="#fff" stroke="#e2e8f0"/><text x="24" y="21" textAnchor="middle" fill="#1A1F71" fontSize="13" fontWeight="bold" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text></svg>
                      <svg viewBox="0 0 48 32" className="h-5 w-auto"><rect width="48" height="32" rx="4" fill="#252525"/><circle cx="19" cy="16" r="9" fill="#EB001B"/><circle cx="29" cy="16" r="9" fill="#F79E1B"/><path d="M24 9.34a9 9 0 0 1 0 13.32 9 9 0 0 1 0-13.32z" fill="#FF5F00"/></svg>
                      <svg viewBox="0 0 48 32" className="h-5 w-auto"><rect width="48" height="32" rx="4" fill="#006FCF"/><text x="24" y="21" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold" fontFamily="Arial, sans-serif">AMEX</text></svg>
                    </div>
                    <Button type="button" variant="ghost" className="w-full text-[#94A3B8] text-sm" onClick={() => setStep('form')}>Retour</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Advantages */}
      <section className="py-16 bg-[#F8F9FA]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-[#0A2540] text-center mb-10">
            Des vacances <span className="italic text-[#2A9D8F]">tout inclus</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { icon: Ship, title: 'Hebergement', desc: 'Cabine double tout confort' },
              { icon: Utensils, title: 'Repas', desc: 'Pension complète - produits locaux' },
              { icon: Wine, title: 'Boissons', desc: 'Softs & alcools - vins locaux' },
              { icon: Waves, title: 'Activites', desc: 'Paddle, canoe, peche' },
              { icon: Anchor, title: 'Équipage', desc: 'Capitaine & hotesse cook' },
              { icon: Calendar, title: 'Réservation prioritaire', desc: 'Jusqu\'a 180j avant le public' },
              { icon: Star, title: 'Places garanties', desc: '1 ou 2 semaines par an' },
              { icon: Crown, title: 'Report possible', desc: 'Changement de dates si besoin' },
            ].map((f, i) => (
              <div key={i} className="flex gap-3 p-4 bg-white rounded-xl border border-slate-100">
                <div className="w-10 h-10 bg-[#2A9D8F]/10 rounded-lg flex items-center justify-center shrink-0">
                  <f.icon className="h-5 w-5 text-[#2A9D8F]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0A2540] text-xs uppercase">{f.title}</h4>
                  <p className="text-[#94A3B8] text-xs mt-0.5">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligible cruises */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-[#0A2540] text-center mb-8">Croisières eligibles</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'Tour de Corse', slug: 'tour-de-corse-croisiere-catamaran', img: 'https://static.wixstatic.com/media/ce6ce7_170fb96af2764aecb7eb7c526a48eb27~mv2.png/v1/fill/w_300,h_200,q_85/croisiere%20catamaran%20le%20tour%20de%20Corse%20sognudimare.png' },
              { name: 'Corse du Sud', slug: 'croisiere-catamaran-corse-du-sud', img: 'https://static.wixstatic.com/media/ce6ce7_2c02fe160efb49b6930f0c695a53e34f~mv2.png/v1/fill/w_300,h_200,q_85/croisiere%20catamaran%20la%20corse%20du%20sud%20sognudimare.png' },
              { name: 'Ouest Corse', slug: 'croisiere-catamaran-ouest-corse', img: 'https://static.wixstatic.com/media/ce6ce7_bdc5406402ea4be3b94eeeb747d2da1a~mv2.png/v1/fill/w_300,h_200,q_85/croisiere%20catamaran%20ouest%20corse%20sognudimare.png' },
              { name: 'Sardaigne & Corse', slug: 'croisiere-catamaran-sardaigne-corse', img: 'https://static.wixstatic.com/media/ce6ce7_68a8fb4c934c44cb909dfc0075f36d83~mv2.png/v1/fill/w_300,h_200,q_85/croisiere%20catamaran%20la%20sardaigne%20et%20la%20corse%20du%20sud%20sognudimare.png' },
            ].map((c, i) => (
              <Link key={i} to={`/${c.slug}`} className="bg-[#F8F9FA] rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all group">
                <img src={c.img} alt={c.name} className="w-full h-28 object-cover group-hover:scale-105 transition-transform" />
                <div className="p-3 text-center"><p className="font-semibold text-[#0A2540] text-sm">{c.name}</p></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A2540]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-6">Passer du rêve a la réalité...</h2>
          <p className="text-white/70 mb-8">Croisières catamaran en pension complète avec équipage depuis Ajaccio</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#C4724F] hover:bg-[#C4724F]/90 text-white font-semibold" data-testid="club-cta-reserve">
              <Link to="/reservation-cabines-privatisation-catamaran-sognudimare">Réserver ma croisière <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#0A2540]">
              <a href="tel:0495729028">Appeler le 04 95 72 90 28</a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ClubPage;
